import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ComponentType,
  type ReactNode,
  type Ref,
} from 'react';
import { Trash } from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import { Spinner } from '../Spinner/Spinner';

export type ButtonVariant = 'primary' | 'action' | 'secondary' | 'tertiary' | 'danger' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * primary: the one main action on a screen (sun in the next theme).
 * action: actions repeated on every card or row, e.g. "View job" (ink in the next theme).
 * secondary: outlined; tertiary: text-only. danger: no warning hue exists, so it is ink text in a heavy ink
 * outline with a trash icon by default (meaning = icon + wording, MEANING.md). destructive: the confirm button of a
 * destructive dialog, a solid ink fill with a trash icon by default.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-jc-cta text-jc-on-cta hover:bg-jc-cta-hover',
  action: 'bg-jc-action text-jc-on-action hover:bg-jc-action-hover',
  secondary: 'bg-jc-surface text-jc-ink ring-1 ring-inset ring-jc-border-control hover:bg-jc-elevated',
  tertiary: 'bg-transparent text-jc-ink underline underline-offset-4 hover:bg-jc-elevated',
  danger: 'bg-jc-surface text-jc-ink ring-2 ring-inset ring-jc-ink hover:bg-jc-elevated',
  destructive: 'bg-jc-ink text-jc-surface hover:bg-jc-ink-secondary',
};

/**
 * Disabled is its own neutral look, not a faded variant: a faded sun reads as a muddy brown on the dark page and
 * still says "the action" (MEANING.md). Filled variants turn into an elevated pill with secondary text and no
 * outline; tertiary keeps only secondary text, without its link underline. A loading button (aria-busy) keeps its
 * variant: it is the action, in progress. Both prefixes are spelled out so Tailwind sees every class.
 */
const FILLED_DISABLED =
  'disabled:bg-jc-elevated disabled:text-jc-ink-secondary disabled:ring-0 aria-disabled:not-aria-busy:bg-jc-elevated aria-disabled:not-aria-busy:text-jc-ink-secondary aria-disabled:not-aria-busy:ring-0';
const DISABLED: Record<ButtonVariant, string> = {
  primary: FILLED_DISABLED,
  action: FILLED_DISABLED,
  secondary: FILLED_DISABLED,
  danger: FILLED_DISABLED,
  destructive: FILLED_DISABLED,
  tertiary:
    'disabled:bg-transparent disabled:text-jc-ink-secondary disabled:no-underline aria-disabled:not-aria-busy:bg-transparent aria-disabled:not-aria-busy:text-jc-ink-secondary aria-disabled:not-aria-busy:no-underline',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-10 gap-2 px-4 text-sm',
  md: 'h-10 gap-2 px-4 text-sm',
  lg: 'h-12 gap-2 px-6 text-sm',
};

export const buttonClass = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', fullWidth = false) =>
  cx(
    'inline-flex shrink-0 items-center justify-center rounded-jc-control font-semibold leading-none whitespace-nowrap select-none',
    'transition duration-(--jc-duration-fast) ease-jc-out active:scale-97 motion-reduce:transition-none motion-reduce:active:scale-100 motion-reduce:active:opacity-70',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
    'aria-disabled:cursor-not-allowed aria-disabled:active:scale-100 disabled:cursor-not-allowed disabled:active:scale-100',
    VARIANT[variant],
    DISABLED[variant],
    SIZE[size],
    fullWidth && 'w-full',
  );

type Common = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon. Binoculars for follow/watch, the bell only for alerts. */
  icon?: ComponentType<IconProps>;
  /** Shows a spinner, keeps the width, and blocks presses. */
  loading?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
};

export type ButtonProps = Common & ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };
export type ButtonLinkProps = Common & AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; disabled?: boolean };

function Content({ icon, loading, size, children }: Pick<Common, 'icon' | 'loading' | 'size' | 'children'>) {
  const iconSize = size === 'lg' ? 'md' : 'sm';
  return (
    <>
      {loading ? <Spinner size={iconSize} /> : icon ? <Icon icon={icon} size={iconSize} /> : null}
      <span>{children}</span>
    </>
  );
}

/**
 * A pill-shaped action: a <button>, or an <a> when given `href`. Label is the 14/20 label style (sans 600),
 * sentence case, verb first. Looks like today's site until the next theme is switched on.
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps | ButtonLinkProps>(
  function Button(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      icon: iconProp,
      loading = false,
      fullWidth = false,
      className,
      children,
      ...rest
    } = props;
    const icon = iconProp ?? (variant === 'danger' || variant === 'destructive' ? Trash : undefined);
    const classes = cx(buttonClass(variant, size, fullWidth), className);

    if (typeof rest.href === 'string') {
      const { disabled, onClick, ...anchor } = rest as ButtonLinkProps;
      const inert = disabled || loading;
      return (
        <a
          {...anchor}
          ref={ref as Ref<HTMLAnchorElement>}
          className={classes}
          aria-disabled={inert || undefined}
          aria-busy={loading || undefined}
          tabIndex={inert ? -1 : anchor.tabIndex}
          href={inert ? undefined : anchor.href}
          onClick={(e) => {
            if (inert) {
              e.preventDefault();
              return;
            }
            onClick?.(e);
          }}
        >
          <Content icon={icon} loading={loading} size={size}>
            {children}
          </Content>
        </a>
      );
    }

    const { type = 'button', disabled, ...button } = rest as ButtonProps;
    return (
      <button
        {...button}
        ref={ref as Ref<HTMLButtonElement>}
        type={type}
        className={classes}
        disabled={disabled}
        aria-disabled={loading || undefined}
        aria-busy={loading || undefined}
        onClick={loading ? (e) => e.preventDefault() : button.onClick}
      >
        <Content icon={icon} loading={loading} size={size}>
          {children}
        </Content>
      </button>
    );
  },
);
